import { create } from 'zustand';
import { apiService } from '../services/api';

interface ProcessingFile {
  fileId: string;
  filename: string;
  stage: string;
  progress: number;
  message?: string;
  startedAt: string;
}

interface ProcessingState {
  activeProcess: ProcessingFile | null;
  pollInterval: NodeJS.Timeout | null;
  
  // Actions
  setActiveProcess: (fileId: string, filename: string) => void;
  clearActiveProcess: () => void;
  checkActiveProcesses: () => Promise<void>;
  startPolling: () => void;
  stopPolling: () => void;
}

export const useProcessingStore = create<ProcessingState>((set, get) => ({
  activeProcess: null,
  pollInterval: null,

  setActiveProcess: (fileId: string, filename: string) => {
    set({
      activeProcess: {
        fileId,
        filename,
        stage: 'processing',
        progress: 0,
        startedAt: new Date().toISOString(),
      }
    });
    
    // Start polling for this process
    get().startPolling();
  },

  clearActiveProcess: () => {
    get().stopPolling();
    set({ activeProcess: null });
  },

  checkActiveProcesses: async () => {
    try {
      // Check for any active processes
      const response = await apiService.get('/api/sov/files/processing');
      const activeProcesses = response.active_processes || [];
      
      if (activeProcesses.length > 0) {
        // Take the first one (sequential processing)
        const process = activeProcesses[0];
        set({
          activeProcess: {
            fileId: process.file_id,
            filename: process.filename,
            stage: process.stage,
            progress: process.progress,
            message: process.message,
            startedAt: process.started_at,
          }
        });
        
        // Start polling if not already
        const { pollInterval } = get();
        if (!pollInterval) {
          get().startPolling();
        }
      } else {
        // No active processes
        set({ activeProcess: null });
        get().stopPolling();
      }
    } catch (error) {
      console.error('Failed to check active processes:', error);
    }
  },

  startPolling: () => {
    const { pollInterval } = get();
    if (pollInterval) return; // Already polling
    
    // Poll every 2 seconds
    const interval = setInterval(async () => {
      const { activeProcess } = get();
      if (!activeProcess) {
        get().stopPolling();
        return;
      }
      
      try {
        const status = await apiService.get(`/api/sov/files/${activeProcess.fileId}/status`);
        
        if (status.stage === 'completed' || status.stage === 'failed') {
          // Processing complete
          get().clearActiveProcess();
        } else {
          // Update progress
          set({
            activeProcess: {
              ...activeProcess,
              stage: status.stage,
              progress: status.progress,
              message: status.message,
            }
          });
        }
      } catch (error) {
        console.error('Failed to fetch process status:', error);
      }
    }, 2000);
    
    set({ pollInterval: interval });
  },

  stopPolling: () => {
    const { pollInterval } = get();
    if (pollInterval) {
      clearInterval(pollInterval);
      set({ pollInterval: null });
    }
  },
}));